import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MarkdownText } from "../components/MarkdownText";
import { toast } from "sonner";
import { MessageCircle, Settings, Send, X, MoonStar, ArrowLeft, Trash2 } from "lucide-react";

const TONES = [
  { value: "diretto", label: "Diretto" },
  { value: "rassicurante", label: "Rassicurante" },
  { value: "riflessivo", label: "Riflessivo" },
  { value: "ironico", label: "Ironico / leggero" },
];
const GENDERS = [
  { value: "femminile", label: "Femminile" },
  { value: "maschile", label: "Maschile" },
  { value: "neutro", label: "Neutro" },
];

export default function Assistente() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const tipo = params.get("tipo");
  const sourceId = params.get("id");

  const [profile, setProfile] = useState(null);
  const [chat, setChat] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [error, setError] = useState("");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmMsgIdx, setConfirmMsgIdx] = useState(null);
  const [confirmChatDelete, setConfirmChatDelete] = useState(false);
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  const assistantName = "Arcana";

  useEffect(() => {
    if (!user || !tipo || !sourceId) return;
    (async () => {
      try {
        const p = await api.get("/assistant/profile");
        setProfile(p.data);
        const { data } = await api.post("/assistant/open", { source_type: tipo, source_id: sourceId });
        setChat(data);
        setRemaining(data.remaining);
      } catch (e) {
        setError(fmtErr(e, "Non riesco ad aprire la conversazione con Arcana."));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tipo, sourceId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat?.messages?.length, busy]);

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/assistant/profile", {
        tone: profile.tone, gender: profile.gender,
        memory_enabled: profile.memory_enabled, ...patch,
      });
      setProfile(data);
      toast.success("Impostazioni salvate.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const resetMemory = async () => {
    try {
      await api.post("/assistant/memory/reset");
      setConfirmReset(false);
      toast.success("Memoria personale cancellata.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteMessage = async (idx) => {
    try {
      await api.delete(`/assistant/chats/${chat.chat_id}/messages/${idx}`);
      setChat((c) => ({ ...c, messages: c.messages.filter((_, j) => j !== idx) }));
      setConfirmMsgIdx(null);
      toast.success("Messaggio eliminato.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deleteChat = async () => {
    try {
      await api.delete(`/assistant/chats/${chat.chat_id}`);
      toast.success("Conversazione eliminata.");
      navigate("/letture");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy || remaining === 0) return;
    setBusy(true);
    setInput("");
    const base = chat.messages || [];
    setChat({ ...chat, messages: [...base, { role: "user", content: text }] });
    try {
      const { data } = await api.post("/assistant/message", { chat_id: chat.chat_id, text });
      setChat({ ...chat, messages: [...base, { role: "user", content: text }, { role: "assistant", content: data.reply }] });
      setRemaining(data.remaining);
    } catch (e) {
      setChat({ ...chat, messages: base });
      if (e?.response?.status === 403) {
        setRemaining(0);
      } else {
        toast.error(fmtErr(e, "Messaggio non inviato. Riprova."));
      }
      setInput(text);
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/cliente");
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="assistant-login-required">
        <div className="mystic-card p-10">
          <MoonStar className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <p className="text-purple-200/70 text-sm mb-8">Per parlare con Arcana è necessario accedere.</p>
          <Link to="/accedi?redirect=/cliente" className="gold-btn inline-block px-8 py-3 text-sm">Accedi o registrati</Link>
        </div>
      </div>
    );
  }

  if (!tipo || !sourceId) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="assistant-no-context">
        <div className="mystic-card p-10">
          <p className="text-purple-200/70 text-sm">Arcana si apre dal risultato di una consultazione: completa una lettura e poi approfondiscila con lei.</p>
          <Link to="/" className="text-amber-300 text-sm mt-4 inline-block">← Torna alla Home</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="assistant-error">
        <div className="mystic-card p-10">
          <p className="text-purple-200/70 text-sm">{error}</p>
          <Link to="/letture" className="text-amber-300 text-sm mt-4 inline-block">← Le tue letture</Link>
        </div>
      </div>
    );
  }

  if (!profile || !chat) {
    return <p className="text-center text-purple-300/50 text-sm py-24" data-testid="assistant-loading">Sto preparando la conversazione…</p>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 lg:px-8 py-10 sm:py-14 flex flex-col min-h-[calc(100vh-8rem)]" data-testid="assistant-page">
      <button onClick={goBack} data-testid="assistant-back-btn" className="self-start inline-flex items-center gap-2 text-xs text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-4 py-2 mb-5 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Torna alla lettura
      </button>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full border border-amber-500/40 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-amber-400" />
          </span>
          <div>
            <h1 className="font-display text-2xl text-amber-100" data-testid="assistant-name-display">{assistantName}</h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-purple-300/50">Assistente di ARCANA</p>
          </div>
        </div>
        <button onClick={() => { setShowSettings(true); setConfirmReset(false); setConfirmChatDelete(false); }} data-testid="assistant-settings-btn" className="p-2.5 rounded-full border border-[#4a2175] text-purple-300/70 hover:text-amber-300 hover:border-amber-500/50 transition-colors" aria-label="Impostazioni assistente">
          <Settings className="w-4 h-4" />
        </button>
      </div>

      <div className="mystic-card px-5 py-3 mb-4" data-testid="assistant-context">
        <p className="text-[10px] uppercase tracking-[0.2em] text-purple-300/50">Approfondimento con Arcana</p>
        <p className="text-xs text-purple-200/70 mt-1">
          <span className="text-amber-300/90">{chat.context.method_label}</span> · “{chat.context.question}”
        </p>
      </div>

      <p className="text-xs text-amber-300/80 text-center mb-4" data-testid="assistant-counter">
        approfondimenti disponibili: {remaining ?? "…"}
      </p>

      <div className="flex-1 space-y-4 mb-5" data-testid="assistant-messages">
        {chat.messages.length === 0 && (
          <div className="mystic-card p-6" data-testid="assistant-welcome">
            <p className="text-sm text-purple-100/80 leading-relaxed">
              Sono qui per ragionare con te su ciò che è emerso. Puoi chiedermi di chiarire una parte del responso,
              raccontarmi com'è andata una situazione, o semplicemente parlarne. Da dove vuoi cominciare?
            </p>
          </div>
        )}
        {chat.messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${i}`}>
            <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm leading-relaxed ${
              m.role === "user"
                ? "bg-amber-500/15 border border-amber-500/30 text-amber-50"
                : "bg-[#180a26] border border-[#4a2175] text-purple-100/85"
            }`}>
              {m.role === "assistant" && <p className="text-[10px] uppercase tracking-[0.18em] text-amber-400/60 mb-1.5">{assistantName}</p>}
              <p className="whitespace-pre-line"><MarkdownText text={m.content} /></p>
              <div className="mt-1.5 flex justify-end items-center gap-2">
                {confirmMsgIdx === i ? (
                  <button onClick={() => deleteMessage(i)} data-testid={`assistant-msg-delete-confirm-${i}`} className="inline-flex items-center gap-1 text-[10px] text-red-300 bg-red-500/10 border border-red-400/50 rounded-full px-2.5 py-1">
                    <Trash2 className="w-3 h-3" /> Confermi?
                  </button>
                ) : (
                  <button onClick={() => { setConfirmMsgIdx(i); setTimeout(() => setConfirmMsgIdx(null), 4000); }} data-testid={`assistant-msg-delete-${i}`} aria-label="Elimina messaggio" className="p-1 text-purple-300/40 hover:text-red-300 transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="assistant-typing">
            <div className="bg-[#180a26] border border-[#4a2175] rounded-2xl px-5 py-3.5">
              <p className="text-xs text-purple-300/60 animate-pulse">{assistantName} sta scrivendo…</p>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {remaining === 0 ? (
        <div className="mystic-card p-6 text-center" data-testid="assistant-ended-notice">
          <p className="text-sm text-purple-100/85">Hai usato i 2 approfondimenti inclusi in questa consultazione.</p>
          <p className="text-xs text-purple-300/60 mt-2 leading-relaxed">
            Ogni nuova consultazione include 2 nuovi approfondimenti. E se desideri un'analisi
            davvero personale della tua situazione, il consulto privato con Virginia è un'esperienza diversa e dedicata.
          </p>
        </div>
      ) : (
        <div className="flex gap-2" data-testid="assistant-input-row">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            maxLength={800}
            placeholder="Scrivi qui…"
            disabled={busy}
            data-testid="assistant-input"
            className="input-dark flex-1"
          />
          <button onClick={send} disabled={busy || !input.trim()} data-testid="assistant-send-btn" className="gold-btn px-5 flex items-center justify-center disabled:opacity-50" aria-label="Invia">
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}

      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setShowSettings(false)} data-testid="assistant-settings-overlay">
          <div className="mystic-card max-w-sm w-full p-7 relative max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assistant-settings-panel">
            <button onClick={() => setShowSettings(false)} data-testid="assistant-settings-close" className="absolute top-4 right-4 text-purple-300/60 hover:text-amber-300 transition-colors" aria-label="Chiudi">
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-display text-xl text-amber-100 mb-5">Impostazioni</h2>

            <p className="text-xs text-purple-300/60 mb-2">Tono della conversazione</p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {TONES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => saveSettings({ tone: t.value })}
                  data-testid={`assistant-tone-${t.value}`}
                  className={`text-xs rounded-full border px-3 py-2 transition-colors ${
                    profile.tone === t.value ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] text-purple-300/70 hover:border-amber-500/40"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <p className="text-xs text-purple-300/60 mb-2">Presentazione</p>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  onClick={() => saveSettings({ gender: g.value })}
                  data-testid={`assistant-gender-${g.value}`}
                  className={`text-xs rounded-full border px-3 py-2 transition-colors ${
                    profile.gender === g.value ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] text-purple-300/70 hover:border-amber-500/40"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="text-xs text-purple-200/80">Memoria personale</p>
                <p className="text-[10px] text-purple-300/50 mt-0.5 leading-relaxed">
                  Se attiva, l'assistente ricorda tra le conversazioni solo ciò che gli chiedi esplicitamente di ricordare.
                </p>
              </div>
              <button
                onClick={() => saveSettings({ memory_enabled: !profile.memory_enabled })}
                data-testid="assistant-memory-toggle"
                className={`w-11 h-6 rounded-full border transition-colors shrink-0 relative ${
                  profile.memory_enabled ? "bg-amber-500/30 border-amber-500/60" : "bg-[#180a26] border-[#4a2175]"
                }`}
                aria-label="Attiva o disattiva la memoria"
              >
                <span className={`absolute top-0.5 w-[18px] h-[18px] rounded-full bg-amber-300 transition-all ${profile.memory_enabled ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>

            <div className="pt-4 mt-4 border-t border-[#4a2175]">
              {!confirmReset ? (
                <button onClick={() => setConfirmReset(true)} data-testid="assistant-memory-reset-btn" className="text-xs text-red-300/80 hover:text-red-300 border border-red-500/40 rounded-full px-4 py-2 transition-colors">
                  Reset memoria
                </button>
              ) : (
                <div data-testid="assistant-memory-confirm">
                  <p className="text-xs text-purple-200/80 mb-3">Vuoi cancellare la memoria personale dell'assistente?</p>
                  <div className="flex gap-3">
                    <button onClick={resetMemory} data-testid="assistant-memory-confirm-yes" className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/50 rounded-full px-4 py-2 transition-colors">
                      Conferma
                    </button>
                    <button onClick={() => setConfirmReset(false)} data-testid="assistant-memory-confirm-no" className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2">
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-4 mt-4 border-t border-[#4a2175]">
              {!confirmChatDelete ? (
                <button onClick={() => setConfirmChatDelete(true)} data-testid="assistant-chat-delete-btn" className="text-xs text-red-300/80 hover:text-red-300 border border-red-500/40 rounded-full px-4 py-2 transition-colors">
                  Elimina questa conversazione
                </button>
              ) : (
                <div data-testid="assistant-chat-delete-confirm">
                  <p className="text-xs text-purple-200/80 mb-3">Eliminare definitivamente l'intera conversazione? La consultazione collegata resta ne «Le mie letture».</p>
                  <div className="flex gap-3">
                    <button onClick={deleteChat} data-testid="assistant-chat-delete-yes" className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/50 rounded-full px-4 py-2 transition-colors">
                      Conferma
                    </button>
                    <button onClick={() => setConfirmChatDelete(false)} data-testid="assistant-chat-delete-no" className="text-xs text-purple-300/70 hover:text-purple-200 px-3 py-2">
                      Annulla
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
